import { BACKERS_PROJECTION, TRADERS_PROJECTION } from '@/lib/sdk/projections/market-detail';

export const MARKET_DETAIL_TABS = ['winner', 'debate', 'traders', 'backers', 'info'] as const;
export type MarketDetailTab = (typeof MARKET_DETAIL_TABS)[number];

const PRE_RESOLUTION_MARKET_DETAIL_TABS: readonly MarketDetailTab[] = [
  'debate',
  'traders',
  'backers',
  'info',
];

const POST_RESOLUTION_MARKET_DETAIL_TABS: readonly MarketDetailTab[] = [
  'winner',
  'traders',
  'debate',
  'backers',
  'info',
];

export function isMarketDetailTab(value: string): value is MarketDetailTab {
  return MARKET_DETAIL_TABS.includes(value as MarketDetailTab);
}

export function parseMarketDetailTab(
  value: string | readonly string[] | null | undefined,
): MarketDetailTab | undefined {
  return typeof value === 'string' && isMarketDetailTab(value) ? value : undefined;
}

export function resolveMarketDetailTab(
  value: string | readonly string[] | null | undefined,
  marketStatus: string | undefined = 'trading',
  selectedTab?: MarketDetailTab,
  hasBackerRewards: boolean = true,
): MarketDetailTab {
  const selected = selectedTab ?? parseMarketDetailTab(value);
  if (
    selected !== undefined &&
    marketDetailTabOrder(marketStatus, hasBackerRewards).includes(selected)
  ) {
    return selected;
  }
  return isPostResolutionMarketStatus(marketStatus) ? 'winner' : 'debate';
}

export function marketDetailTabOrder(
  marketStatus: string | undefined,
  hasBackerRewards: boolean = true,
): readonly MarketDetailTab[] {
  const tabs = isPostResolutionMarketStatus(marketStatus)
    ? POST_RESOLUTION_MARKET_DETAIL_TABS
    : PRE_RESOLUTION_MARKET_DETAIL_TABS;
  return hasBackerRewards ? tabs : tabs.filter((tab) => tab !== 'backers');
}

function isPostResolutionMarketStatus(marketStatus: string | undefined): boolean {
  return marketStatus === 'resolved' || marketStatus === 'graduated';
}

export function marketTradersTabQueryArgs(pubkey: string): {
  readonly pubkey: string;
  readonly sortBy: 'volume';
  readonly sortOrder: 'desc';
  readonly fields: typeof TRADERS_PROJECTION;
} {
  return {
    pubkey,
    sortBy: 'volume' as const,
    sortOrder: 'desc' as const,
    fields: TRADERS_PROJECTION,
  };
}

export function marketBackersTabQueryArgs(
  pubkey: string,
  sort: string | null | undefined,
): {
  readonly pubkey: string;
  readonly sortBy: 'amount' | 'shares' | 'recent' | 'date' | 'earnings';
  readonly sortOrder: 'desc';
  readonly fields: typeof BACKERS_PROJECTION;
} {
  return {
    pubkey,
    sortBy:
      sort === 'shares' || sort === 'recent' || sort === 'date' || sort === 'earnings'
        ? sort
        : ('amount' as const),
    sortOrder: 'desc' as const,
    fields: BACKERS_PROJECTION,
  };
}
